"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Copy, Download, RotateCcw, CheckCircle, XCircle, AlertTriangle, Code } from "lucide-react";
import { toast } from "react-hot-toast";

export default function JSONFormatterTool() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const formatJSON = () => {
    if (!inputText.trim()) {
      setErrorMessage("Please enter JSON to format");
      setIsValid(false);
      return;
    }

    try {
      const parsed = JSON.parse(inputText);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutputText(formatted);
      setIsValid(true);
      setErrorMessage("");
    } catch (error) {
      setIsValid(false);
      setErrorMessage("Invalid JSON format");
    }
  };

  const minifyJSON = () => {
    if (!inputText.trim()) {
      setErrorMessage("Please enter JSON to minify");
      setIsValid(false);
      return;
    }

    try {
      const parsed = JSON.parse(inputText);
      const minified = JSON.stringify(parsed);
      setOutputText(minified);
      setIsValid(true);
      setErrorMessage("");
    } catch (error) {
      setIsValid(false);
      setErrorMessage("Invalid JSON format");
    }
  };

  const validateJSON = () => {
    if (!inputText.trim()) {
      setErrorMessage("Please enter JSON to validate");
      setIsValid(false);
      return;
    }

    try {
      JSON.parse(inputText);
      setIsValid(true);
      setErrorMessage("");
      toast.success("JSON is valid!");
    } catch (error) {
      setIsValid(false);
      setErrorMessage("Invalid JSON format");
      toast.error("JSON is invalid!");
    }
  };

  const copyToClipboard = () => {
    if (!outputText) return;
    navigator.clipboard.writeText(outputText)
      .then(() => toast.success("JSON copied to clipboard!"))
      .catch(() => toast.error("Failed to copy JSON."));
  };

  const downloadJSON = () => {
    if (!outputText) return;
    const blob = new Blob([outputText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "formatted.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("JSON downloaded!");
  };

  const clearAll = () => {
    setInputText("");
    setOutputText("");
    setIsValid(null);
    setErrorMessage("");
  };

  const loadSample = () => {
    const sample = {
      "name": "John Doe",
      "age": 30,
      "email": "john@example.com",
      "address": {
        "street": "123 Main St",
        "city": "New York",
        "zipCode": "10001"
      },
      "hobbies": ["reading", "swimming", "coding"],
      "isActive": true
    };
    setInputText(JSON.stringify(sample));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Input JSON
              {isValid === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {isValid === false && <XCircle className="h-5 w-5 text-red-500" />}
            </CardTitle>
            <CardDescription>
              Enter or paste your JSON data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Enter JSON data here..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="min-h-[300px] font-mono text-sm"
            />
            
            <div className="flex flex-wrap gap-2">
              <Button onClick={formatJSON} className="btn-enhanced hover-lift">
                Format JSON
              </Button>
              <Button onClick={minifyJSON} variant="outline" className="hover-lift">
                Minify JSON
              </Button>
              <Button onClick={validateJSON} variant="outline" className="hover-lift">
                Validate JSON
              </Button>
              <Button onClick={loadSample} variant="outline" className="hover-lift">
                Load Sample
              </Button>
              <Button onClick={clearAll} variant="outline" className="hover-lift">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>

            {errorMessage && (
              <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {errorMessage}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>Formatted JSON</CardTitle>
            <CardDescription>
              Your formatted JSON will appear here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Formatted JSON will appear here..."
              value={outputText}
              readOnly
              className="min-h-[300px] font-mono text-sm bg-muted/50"
            />
            
            <div className="flex gap-2">
              <Button onClick={copyToClipboard} variant="outline" className="hover-lift" disabled={!outputText}>
                <Copy className="h-4 w-4 mr-2" />
                Copy JSON
              </Button>
              <Button onClick={downloadJSON} variant="outline" className="hover-lift" disabled={!outputText}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="card-enhanced">
        <CardHeader>
          <CardTitle>JSON Tools Information</CardTitle>
          <CardDescription>
            Learn about JSON formatting and validation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Format</div>
              <div className="text-sm text-muted-foreground">Pretty print with indentation</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Minify</div>
              <div className="text-sm text-muted-foreground">Remove whitespace and newlines</div>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-primary">Validate</div>
              <div className="text-sm text-muted-foreground">Check for syntax errors</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
